import React from 'react';
import { theme } from '../../styles/theme';

const ConfirmModal = ({ 
  isOpen, 
  onConfirm, 
  onCancel, 
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'warning' // 'warning', 'danger', 'info'
}) => {
  if (!isOpen) return null;

  const typeColors = {
    warning: {
      icon: '⚠',
      color: theme.colors.warning || '#f59e0b',
      iconBg: 'rgba(245, 158, 11, 0.1)',
    },
    danger: {
      icon: '⚠',
      color: theme.colors.error || '#ef4444',
      iconBg: 'rgba(239, 68, 68, 0.1)',
    },
    info: {
      icon: 'ℹ',
      color: theme.colors.primary || '#3b82f6',
      iconBg: 'rgba(59, 130, 246, 0.1)',
    },
  };

  const currentType = typeColors[type] || typeColors.warning;

  const overlayStyles = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    animation: 'fadeIn 0.2s ease-in',
    padding: '1rem',
  };

  const modalStyles = {
    backgroundColor: theme.colors.background || '#1a1a1a',
    borderRadius: theme.borderRadius?.lg || '12px',
    padding: 'clamp(1.25rem, 4vw, 2rem)',
    maxWidth: '500px',
    width: '100%',
    maxHeight: 'calc(100vh - 2rem)',
    overflowY: 'auto',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    border: `1px solid ${theme.colors.border || '#333'}`,
    animation: 'slideUp 0.3s ease-out',
  };

  const headerStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1.5rem',
  };

  const iconStyles = {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    backgroundColor: currentType.iconBg,
    color: currentType.color,
    flexShrink: 0,
  };

  const titleStyles = {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: theme.colors.text || '#ffffff',
    margin: 0,
  };

  const messageStyles = {
    fontSize: '1rem',
    lineHeight: '1.6',
    color: theme.colors.textSecondary || '#a0a0a0',
    marginBottom: '2rem',
    whiteSpace: 'pre-line',
  };

  const buttonContainerStyles = {
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
  };

  const buttonBaseStyles = {
    padding: '0.75rem 1.5rem',
    borderRadius: theme.borderRadius?.md || '8px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: 'none',
    minWidth: '100px',
    flex: '1 1 auto',
  };

  const cancelButtonStyles = {
    ...buttonBaseStyles,
    backgroundColor: 'transparent',
    color: theme.colors.textSecondary || '#a0a0a0',
    border: `1px solid ${theme.colors.border || '#333'}`,
  };

  const confirmButtonStyles = {
    ...buttonBaseStyles,
    backgroundColor: currentType.color,
    color: '#ffffff',
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { 
              opacity: 0;
              transform: translateY(20px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      <div style={overlayStyles} onClick={onCancel}>
        <div style={modalStyles} onClick={(e) => e.stopPropagation()}>
          <div style={headerStyles}>
            <div style={iconStyles}>
              {currentType.icon}
            </div>
            <h2 style={titleStyles}>{title}</h2>
          </div>
          
          <p style={messageStyles}>{message}</p>
          
          <div style={buttonContainerStyles}>
            {cancelText && (
              <button
                style={cancelButtonStyles}
                onClick={onCancel}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                {cancelText}
              </button>
            )}
            <button
              style={confirmButtonStyles}
              onClick={onConfirm}
              onMouseEnter={(e) => {
                e.target.style.opacity = '0.9';
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = '1';
              }}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConfirmModal;

